import { Auth } from 'aws-amplify';
import React, { useState } from 'react';
import PasswordChecklist from 'react-password-checklist';
import { Navigate, useLocation } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import './styles.scss';
import { I18n, Translate } from '@utils/i18n';

const ResetPasswordV2: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [redirect, setRedirect] = useState<boolean>(false);
  const confirmCodeRef = React.useRef<HTMLInputElement>(null);
  const [passwordInput, setPasswordInput] = React.useState<string>('');
  const [confirmPasswordInput, setConfirmPasswordInput] =
    React.useState<string>('');
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const location = useLocation();

  const handleResetPassword = (event: React.FormEvent) => {
    setLoading(true);
    event.preventDefault();
    if (confirmCodeRef.current !== null) {
      const code = confirmCodeRef.current.value;
      const password = passwordInput;
      if (isPasswordValid) {
        const query = new URLSearchParams(location.search);
        const email = query.get('email') || '';

        Auth.forgotPasswordSubmit(email.trim(), code.trim(), password.trim())
          .then(() => {
            toast.success(I18n.t('password_reset_successful'), {
              autoClose: 3000,
              onClose: () => {
                setRedirect(true);
              }
            });
          })
          .catch((err) => {
            toast.error(`${I18n.t('error_resetting_password')} ${err.message}`);
            setLoading(false);
          });
      } else {
        toast.error(I18n.t('password_dont_match_policy'));
        setLoading(false);
      }
    } else {
      toast.error(I18n.t('fields_cant_be_empty'));
      setLoading(false);
    }
  };

  return (
    <div className="py-7 px-5">
      <div className="card-title mb-4">
        <strong>
          <Translate value="reset_password" />
        </strong>
      </div>
      <form
        id="login-form"
        className="login-form-1"
        onSubmit={handleResetPassword}
      >
        <div>
          <label htmlFor="lg_confirmCode" className="input__label">
            <Translate value="verification_code" />
          </label>
          <div className="input-group py-1">
            <input
              type="text"
              className="form-control"
              id="lg_confirmCode"
              name="confirmCode"
              placeholder="123456"
              required
              ref={confirmCodeRef}
            />
          </div>
        </div>
        <div className="mt-1 info__text">
          <Translate value="check_your_email_code" />
        </div>
        <div className="mt-4">
          <label htmlFor="lg_password" className="input__label">
            <Translate value="new_password" />
          </label>
          <div className="input-group py-1">
            <input
              type="password"
              className="form-control"
              id="lg_password"
              name="password"
              placeholder={I18n.t('new_password')}
              required
              onChange={(e) => setPasswordInput(e.target.value)}
            />
          </div>
        </div>
        <div className="mt-4 mb-4">
          <label htmlFor="lg_confirm_password" className="input__label">
            <Translate value="confirm_password" />
          </label>
          <div className="input-group py-1">
            <input
              type="password"
              className="form-control"
              id="lg_confirm_password"
              name="confirm_password"
              placeholder={I18n.t('confirm_password')}
              required
              onChange={(e) => setConfirmPasswordInput(e.target.value)}
            />
          </div>
        </div>
        {passwordInput.length > 0 && (
          <div
            className="password-checklist-container"
            style={
              !isPasswordValid
                ? { borderColor: '#ff003396' }
                : { borderColor: '#4bd1a0b2' }
            }
          >
            <PasswordChecklist
              rules={['minLength', 'number', 'match']}
              minLength={8}
              value={passwordInput}
              valueAgain={confirmPasswordInput}
              onChange={(isValid) => setIsPasswordValid(isValid)}
              messages={{
                minLength: I18n.t('password_check_minLength', { minLength: 8 }),
                number: I18n.t('password_check_number'),
                match: I18n.t('password_check_match')
              }}
            />
          </div>
        )}
        <div className="submit-button-container text-center mt-4">
          <button type="submit" className="btn mt-2 submit-button px-4">
            {loading ? (
              <ClipLoader size={20} loading color={'#fff'} />
            ) : (
              I18n.t('change_password')
            )}
          </button>
        </div>
      </form>

      {redirect && <Navigate to={{ pathname: '/login' }} replace />}
    </div>
  );
};

export default ResetPasswordV2;
